import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    CircularProgress,
    TextField,
    InputAdornment,
    IconButton,
    Alert,
    FormControlLabel,
    Switch,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import Iconify from "../iconify";
import { enqueueSnackbar } from "notistack";

const ConfirmationDialog = ({
    open,
    onConfirm,
    onClose,
    title,
    message,
    confirmText = "Confirm",
    confirmColor = "primary",
    cancelText = "Cancel",
    loading = false,
    item,
    addQtyField = false,
    showWasteToggle = false,
}) => {
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [qty, setQty] = useState(addQtyField);
    const [qtyError, setQtyError] = useState(false);
    const [note, setNote] = useState("");
    const [waste, setWaste] = useState(false);

    const requiresPassword = item?.elevatedPrivileges;

    // Reset password and qty state when dialog opens/closes
    useEffect(() => {
        if (open) {
            setPassword("");
            setShowPassword(false);
            setPasswordError(false);
            setNote("");
            setQty(addQtyField);
            setQtyError(false);
            setWaste(false);
        }
    }, [open, addQtyField]);

    const handleConfirm = () => {
        if (requiresPassword && !password.trim()) {
            setPasswordError(true);
            return;
        }
        if (requiresPassword && !note.trim()) {
            enqueueSnackbar("Note is required", {
                variant: "error",
            });
            return;
        }

        if (addQtyField && (qty <= 0 || !Number.isInteger(qty))) {
            setQtyError(true);
            return;
        }
        
        if (requiresPassword && addQtyField) {
            onConfirm(password, qty, note, waste);
        } else if (requiresPassword) {
            onConfirm(password, null, note, waste);
        } else if (addQtyField) {
            onConfirm(null, qty, note, waste);
        } else {
            onConfirm(waste);
        }
    };

    const handleClose = () => {
        setPassword("");
        setShowPassword(false);
        setPasswordError(false);
        setQty(1);
        setQtyError(false);
        setWaste(false);
        onClose();
    };

    const handleKeyUp = (event) => {
        if (event.key === "Enter") {
            handleConfirm();
        }
    };

    const handleQtyChange = (e) => {
        const value = parseInt(e.target.value);
        setQty(value);
        setQtyError(false);
    };

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                {message && (
                    <DialogContentText
                        sx={{ mb: requiresPassword || addQtyField || showWasteToggle ? 2 : 0 }}
                    >
                        {message}
                    </DialogContentText>
                )}

                {addQtyField && (
                    <TextField
                        margin="dense"
                        label="Quantity"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={qty}
                        onChange={handleQtyChange}
                        onKeyUp={handleKeyUp}
                        error={qtyError}
                        helperText={
                            qtyError
                                ? "Please enter a valid quantity (positive integer)"
                                : ""
                        }
                        disabled={loading}
                        inputProps={{
                            min: 1,
                            step: 1,
                        }}
                        sx={{ mb: requiresPassword || showWasteToggle ? 2 : 0 }}
                    />
                )}

                {showWasteToggle && (
                    <FormControlLabel
                        control={
                            <Switch
                                checked={waste}
                                onChange={(e) => setWaste(e.target.checked)}
                                disabled={loading}
                            />
                        }
                        label="Mark as Waste"
                        sx={{ mb: requiresPassword ? 2 : 0 }}
                    />
                )}

                {requiresPassword && (
                    <>
                        <TextField
                            autoFocus={!addQtyField}
                            margin="dense"
                            label="Note"
                            fullWidth
                            variant="outlined"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            onKeyUp={handleKeyUp}
                        />
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            This action requires elevated privileges. Please
                            enter the master password.
                        </Alert>
                        <TextField
                            autoFocus={!addQtyField}
                            margin="dense"
                            label="Master Password"
                            type={showPassword ? "text" : "password"}
                            fullWidth
                            variant="outlined"
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                setPasswordError(false);
                            }}
                            onKeyUp={handleKeyUp}
                            error={passwordError}
                            helperText={
                                passwordError
                                    ? "Master password is required"
                                    : ""
                            }
                            disabled={loading}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() =>
                                                setShowPassword(!showPassword)
                                            }
                                            edge="end"
                                            disabled={loading}
                                        >
                                            <Iconify
                                                icon={
                                                    showPassword
                                                        ? "eva:eye-fill"
                                                        : "eva:eye-off-fill"
                                                }
                                            />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    disabled={loading}
                >
                    {cancelText}
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color={confirmColor}
                    autoFocus={!requiresPassword && !addQtyField}
                    disabled={
                        loading ||
                        (requiresPassword && !password.trim() && !note.trim()) ||
                        (addQtyField && (qty <= 0 || !Number.isInteger(qty)))
                    }
                    startIcon={
                        loading ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : null
                    }
                >
                    {loading ? "Processing..." : confirmText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmationDialog;
